import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Radio,
  RefreshCw,
  Search,
  CheckCircle2,
  AlertTriangle,
  RotateCw,
  Power,
  Zap,
  Activity,
  Layers,
  Sparkles,
  Thermometer,
  Cpu,
  HardDrive,
  ArrowUp,
  ArrowDown,
  Plus,
  Pencil,
  Trash2,
  Wifi,
  WifiOff,
  Loader2
} from "lucide-react";
import { AddOltDeviceModal } from "../components/common/AddOltDeviceModal";
import { useAuth } from "../context/AuthContext";

export const JaringanOltPage = () => {
  const { user } = useAuth();
  const canManageOlt = user?.role === "admin" || user?.role === "finance";
  const [data, setData] = useState({ olts: [], onus: [], optical_stats: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterState, setFilterState] = useState("ALL");
  const [rebootingId, setRebootingId] = useState(null);
  const [notification, setNotification] = useState("");
  const [realOlts, setRealOlts] = useState([]);
  const [showAddOlt, setShowAddOlt] = useState(false);
  const [editingOlt, setEditingOlt] = useState(null);
  const [testingOltId, setTestingOltId] = useState(null);
  const [pollingOltId, setPollingOltId] = useState(null);
  const [onuPollResult, setOnuPollResult] = useState(null);
  const [autoSyncing, setAutoSyncing] = useState(false);
  const autoPolledRef = useRef(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const fetchRealOlts = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/jaringan/olt/devices`, { withCredentials: true });
      setRealOlts(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/jaringan/olt`, {
        withCredentials: true
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchRealOlts();
    // eslint-disable-next-line
  }, []);

  // Auto-polling OLT saat halaman dibuka (tidak perlu klik manual)
  useEffect(() => {
    if (autoPolledRef.current || realOlts.length === 0) return;
    autoPolledRef.current = true;
    (async () => {
      setAutoSyncing(true);
      for (const olt of realOlts) {
        try {
          await axios.get(`${BACKEND_URL}/api/jaringan/olt/devices/${olt.id}/onus`, { withCredentials: true, timeout: 120000 });
        } catch (err) { /* biarkan, error tersimpan di last_error */ }
      }
      setAutoSyncing(false);
      fetchData();
      fetchRealOlts();
    })();
    // eslint-disable-next-line
  }, [realOlts]);

  const handleTestOlt = async (olt) => {
    setTestingOltId(olt.id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/jaringan/olt/devices/${olt.id}/test`, {}, { withCredentials: true });
      setNotification(`OLT "${olt.nama}" ONLINE — ${res.data.result?.sys_name || res.data.result?.sys_descr || "SNMP OK"} • Uptime: ${res.data.result?.uptime || "-"}`);
      fetchRealOlts();
    } catch (err) {
      setNotification(`OLT "${olt.nama}" GAGAL: ${err.response?.data?.detail || "tidak terjangkau"}`);
      fetchRealOlts();
    } finally {
      setTestingOltId(null);
      setTimeout(() => setNotification(""), 6000);
    }
  };

  const handlePollOnus = async (olt) => {
    setPollingOltId(olt.id);
    setOnuPollResult(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/jaringan/olt/devices/${olt.id}/onus`, { withCredentials: true, timeout: 120000 });
      setOnuPollResult({ olt, ...res.data });
      fetchRealOlts();
      fetchData();
    } catch (err) {
      alert(`Polling ONU gagal: ${err.response?.data?.detail || "OLT tidak terjangkau"}`);
    } finally {
      setPollingOltId(null);
    }
  };

  const handleDeleteOlt = async (olt) => {
    if (!window.confirm(`Hapus OLT "${olt.nama}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/jaringan/olt/devices/${olt.id}`, { withCredentials: true });
      setNotification(`OLT "${olt.nama}" dihapus`);
      setTimeout(() => setNotification(""), 4000);
      fetchRealOlts();
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal menghapus OLT");
    }
  };

  const handleReboot = async (onuId, sn) => {
    setRebootingId(onuId);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/jaringan/olt/reboot-onu/${onuId}`,
        {},
        { withCredentials: true }
      );
      setNotification(`Perintah Remote Reboot sukses dikirim ke ONT SN: ${sn}`);
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      alert("Gagal mengirim perintah reboot ke ONT");
    } finally {
      setRebootingId(null);
    }
  };

  const stats = data.optical_stats || {};

  const filteredOnus = (data.onus || []).filter((o) => {
    const matchesSearch =
      o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      o.sn?.toLowerCase().includes(search.toLowerCase()) ||
      o.odp_kode?.toLowerCase().includes(search.toLowerCase()) ||
      o.no_layanan?.toLowerCase().includes(search.toLowerCase());

    if (filterState === "ALL") return matchesSearch;
    if (filterState === "GOOD") return matchesSearch && o.optical_state === "good";
    if (filterState === "WARNING") return matchesSearch && o.optical_state === "warning";
    if (filterState === "CRITICAL") return matchesSearch && o.optical_state === "critical";
    if (filterState === "OFFLINE") return matchesSearch && o.optical_state === "offline";
    return matchesSearch;
  });

  return (
    <div className="space-y-6" data-testid="olt-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 text-[10px] font-bold uppercase tracking-wider">
              GPON / EPON Optical Monitoring
            </span>
            <span className="text-xs text-slate-400">Standard ITU-T G.984</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">
            Monitoring OLT & Redaman Optik (ONU/ONT)
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Pantau kualitas redaman kabel fiber optik pelanggan (-18 dBm s/d -24 dBm) & status ONT secara real-time.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {canManageOlt && (
            <button
              onClick={() => { setEditingOlt(null); setShowAddOlt(true); }}
              data-testid="add-olt-btn"
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-purple-600/30 flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              <span>Tambah OLT</span>
            </button>
          )}
          <button
            onClick={() => { fetchData(); fetchRealOlts(); }}
            data-testid="refresh-olt-btn"
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-xl border border-slate-700 flex items-center gap-2 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span>Refresh OLT Polling</span>
          </button>
        </div>
      </div>

      {/* OLT Fisik (SNMP Live) */}
      <div className="space-y-3" data-testid="real-olts-section">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-gradient-to-b from-purple-400 to-fuchsia-500 rounded-full" />
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Radio className="w-4 h-4 text-purple-400" />
            OLT Fisik Terdaftar (SNMP Live)
          </h2>
          <span className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/30 text-[10px] font-bold uppercase">
            HSGQ • HIOSO • VSOL • C-Data • BDCOM • ZTE • Huawei • FiberHome
          </span>
        </div>

        {realOlts.length === 0 ? (
          <div className="p-5 rounded-2xl bg-[#0e1628] border border-dashed border-slate-700 text-center" data-testid="empty-real-olts">
            <Radio className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-xs text-slate-400 font-semibold">Belum ada OLT fisik terdaftar</p>
            <p className="text-[11px] text-slate-500 mt-1">
              Klik <span className="text-purple-400 font-bold">Tambah OLT</span> untuk menghubungkan OLT GPON/EPON Anda via SNMP (aktifkan SNMP di OLT & whitelist IP server).
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {realOlts.map((olt) => (
              <div key={olt.id} className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl" data-testid={`real-olt-card-${olt.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${olt.last_status === "online" ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
                      {olt.last_status === "online" ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-white text-sm">{olt.nama}</p>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30 uppercase">{olt.vendor}</span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 uppercase">{olt.olt_type}</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${olt.last_status === "online" ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
                          {(olt.last_status || "offline").toUpperCase()}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                        {olt.host}:{olt.snmp_port} • SNMP {olt.snmp_version} • {olt.pon_ports} PON
                        {olt.last_sys_name ? ` • ${olt.last_sys_name}` : ""}
                        {olt.last_uptime ? ` • Up: ${olt.last_uptime}` : ""}
                      </p>
                      <p className="text-[10px] mt-0.5">
                        {olt.mikrotik_router_nama ? (
                          <span className="text-cyan-400 font-semibold">↳ Terhubung ke MikroTik: {olt.mikrotik_router_nama}</span>
                        ) : (
                          <span className="text-slate-500">↳ Belum ditautkan ke router MikroTik (edit OLT untuk menautkan)</span>
                        )}
                      </p>
                      {olt.last_onu_count != null && (
                        <p className="text-[10px] text-slate-500 mt-0.5">Polling terakhir: {olt.last_onu_count} ONU ({olt.last_onu_online} online)</p>
                      )}
                      {olt.last_error && <p className="text-[10px] text-rose-400 mt-0.5">{olt.last_error}</p>}
                      {(olt.last_pon_summary || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`pon-cached-${olt.id}`}>
                          {olt.last_pon_summary.map((p) => (
                            <span key={p.port} className="px-2 py-1 rounded-lg bg-slate-900 border border-slate-800 text-[10px] font-mono">
                              <span className="text-slate-400 font-bold">PON {p.port}:</span>{" "}
                              <span className="text-white font-bold">{p.total} ONU</span>{" "}
                              <span className="text-emerald-400">({p.online} aktif)</span>
                              {p.offline > 0 && <span className="text-rose-400"> · {p.offline} off</span>}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                    <button
                      onClick={() => handleTestOlt(olt)}
                      disabled={testingOltId === olt.id}
                      data-testid={`test-olt-${olt.id}`}
                      className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-cyan-300 text-[10px] font-bold flex items-center gap-1 disabled:opacity-50"
                    >
                      {testingOltId === olt.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
                      Tes SNMP
                    </button>
                    <button
                      onClick={() => handlePollOnus(olt)}
                      disabled={pollingOltId === olt.id}
                      data-testid={`poll-olt-${olt.id}`}
                      className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-emerald-300 text-[10px] font-bold flex items-center gap-1 disabled:opacity-50"
                    >
                      {pollingOltId === olt.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
                      Polling ONU
                    </button>
                    {canManageOlt && (
                      <>
                        <button
                          onClick={() => { setEditingOlt(olt); setShowAddOlt(true); }}
                          title="Edit OLT"
                          data-testid={`edit-olt-${olt.id}`}
                          className="p-1.5 rounded-lg bg-slate-800 hover:bg-amber-900/60 text-amber-400"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteOlt(olt)}
                          title="Hapus OLT"
                          data-testid={`delete-olt-${olt.id}`}
                          className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-900/60 text-slate-400 hover:text-rose-400"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {onuPollResult?.olt?.id === olt.id && (
                  <div className="mt-3 pt-3 border-t border-slate-800" data-testid={`onu-poll-result-${olt.id}`}>
                    <p className="text-[11px] text-slate-400 mb-2">
                      Hasil polling: <span className="text-white font-bold">{onuPollResult.total}</span> ONU/interface
                      (<span className="text-emerald-400">{onuPollResult.online} online</span>, <span className="text-rose-400">{onuPollResult.offline} offline</span>)
                      {onuPollResult.source === "ifTable" && <span className="text-amber-400"> — via tabel interface (OID vendor tidak tersedia di firmware ini)</span>}
                    </p>
                    {onuPollResult.pon_summary?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3" data-testid={`pon-summary-${olt.id}`}>
                        {onuPollResult.pon_summary.map((p) => (
                          <div key={p.port} className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-800 text-center min-w-[86px]" data-testid={`pon-port-${p.port}`}>
                            <p className="text-[9px] text-slate-500 font-bold uppercase">PON {p.port}</p>
                            <p className="text-sm font-black text-white font-mono">{p.total} <span className="text-[9px] text-slate-500 font-normal">ONU</span></p>
                            <p className="text-[10px] font-mono"><span className="text-emerald-400 font-bold">{p.online} aktif</span>{p.offline > 0 && <span className="text-rose-400"> · {p.offline} off</span>}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-800">
                      <table className="w-full text-left text-[11px]">
                        <thead className="bg-slate-950/80 sticky top-0">
                          <tr className="text-slate-400">
                            <th className="py-1.5 px-2">Index</th>
                            <th className="py-1.5 px-2">Nama</th>
                            <th className="py-1.5 px-2">MAC/SN</th>
                            <th className="py-1.5 px-2">Status</th>
                            <th className="py-1.5 px-2">Rx Power</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/60">
                          {onuPollResult.onus.map((o) => (
                            <tr key={o.index}>
                              <td className="py-1.5 px-2 font-mono text-slate-400">{o.index}</td>
                              <td className="py-1.5 px-2 text-slate-200">{o.name}</td>
                              <td className="py-1.5 px-2 font-mono text-cyan-300">{o.mac || "-"}</td>
                              <td className="py-1.5 px-2">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${o.status === "online" ? "bg-emerald-500/20 text-emerald-300" : o.status === "offline" ? "bg-rose-500/20 text-rose-300" : "bg-slate-700/60 text-slate-300"}`}>
                                  {o.status}
                                </span>
                              </td>
                              <td className="py-1.5 px-2 font-mono text-slate-300">{o.rx_power_dbm != null ? `${o.rx_power_dbm} dBm` : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Tambah/Edit OLT */}
      {showAddOlt && (
        <AddOltDeviceModal
          initialData={editingOlt}
          onClose={() => { setShowAddOlt(false); setEditingOlt(null); }}
          onSuccess={fetchRealOlts}
        />
      )}

      {notification && (
        <div className="p-3 bg-purple-500/10 border border-purple-500/30 text-purple-300 rounded-xl text-xs flex items-center gap-2 animate-fadeIn">
          <CheckCircle2 className="w-4 h-4 text-purple-400" />
          <span>{notification}</span>
        </div>
      )}

      {/* Optical Signal Health Counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button
          onClick={() => setFilterState("GOOD")}
          className={`p-3.5 rounded-xl border text-left transition-all ${
            filterState === "GOOD"
              ? "bg-emerald-950/40 border-emerald-500/60 ring-1 ring-emerald-500"
              : "bg-[#0e1628] border-slate-800 hover:border-emerald-500/40"
          }`}
        >
          <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
            <span>Bagus (&gt; -24 dBm)</span>
            <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
          </div>
          <p className="text-xl font-bold text-emerald-400 font-mono">
            {stats.good_count || 0} ONU
          </p>
        </button>

        <button
          onClick={() => setFilterState("WARNING")}
          className={`p-3.5 rounded-xl border text-left transition-all ${
            filterState === "WARNING"
              ? "bg-amber-950/40 border-amber-500/60 ring-1 ring-amber-500"
              : "bg-[#0e1628] border-slate-800 hover:border-amber-500/40"
          }`}
        >
          <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
            <span>Waspada (-24 s/d -27)</span>
            <span className="w-2 h-2 rounded-full bg-amber-400"></span>
          </div>
          <p className="text-xl font-bold text-amber-400 font-mono">
            {stats.warning_count || 0} ONU
          </p>
        </button>

        <button
          onClick={() => setFilterState("CRITICAL")}
          className={`p-3.5 rounded-xl border text-left transition-all ${
            filterState === "CRITICAL"
              ? "bg-rose-950/40 border-rose-500/60 ring-1 ring-rose-500"
              : "bg-[#0e1628] border-slate-800 hover:border-rose-500/40"
          }`}
        >
          <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
            <span>Kritis (&lt; -27 dBm)</span>
            <span className="w-2 h-2 rounded-full bg-rose-400"></span>
          </div>
          <p className="text-xl font-bold text-rose-400 font-mono">
            {stats.critical_count || 0} ONU
          </p>
        </button>

        <button
          onClick={() => setFilterState("OFFLINE")}
          className={`p-3.5 rounded-xl border text-left transition-all ${
            filterState === "OFFLINE"
              ? "bg-slate-800 border-slate-600 ring-1 ring-slate-400"
              : "bg-[#0e1628] border-slate-800 hover:border-slate-600"
          }`}
        >
          <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
            <span>Offline / Isolir</span>
            <span className="w-2 h-2 rounded-full bg-slate-500"></span>
          </div>
          <p className="text-xl font-bold text-slate-400 font-mono">
            {stats.offline_count || 0} ONU
          </p>
        </button>
      </div>

      {/* ONU List Table */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-3">
          <div>
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <Layers className="w-4 h-4 text-purple-400" />
              <span>Daftar Optical Network Unit (ONU) Pelanggan</span>
            </h2>
            <p className="text-xs text-slate-400">
            Menampilkan {filteredOnus.length} ONU terbaca dari OLT (hasil polling SNMP terakhir)
            {autoSyncing && <span className="text-cyan-400 font-semibold"> • Sinkronisasi OLT sedang berjalan…</span>}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {filterState !== "ALL" && (
              <button
                onClick={() => setFilterState("ALL")}
                className="text-xs text-cyan-400 hover:underline px-2"
              >
                Reset Filter
              </button>
            )}
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Cari SN, nama, ODP..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 font-semibold">
                <th className="py-2.5 px-3">Pelanggan</th>
                <th className="py-2.5 px-3">Serial Number (SN)</th>
                <th className="py-2.5 px-3">ODP & Uplink Port</th>
                <th className="py-2.5 px-3">Model ONT</th>
                <th className="py-2.5 px-3">Jarak Optik</th>
                <th className="py-2.5 px-3 text-center">Rx Optical Power</th>
                <th className="py-2.5 px-3 text-right">Aksi Remote</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {filteredOnus.map((onu) => {
                const isRebooting = rebootingId === onu.id;
                return (
                  <tr key={onu.id} className="hover:bg-slate-900/40">
                    <td className="py-3 px-3">
                      <p className="font-semibold text-white">{onu.customer_name}</p>
                      <span className="text-[10px] text-slate-500 font-mono">
                        {onu.no_layanan}
                      </span>
                      {onu.olt_nama && (
                        <span className="block text-[9px] text-purple-400/80 mt-0.5">{onu.olt_nama}</span>
                      )}
                    </td>
                    <td className="py-3 px-3 font-mono font-medium text-cyan-300">
                      {onu.sn}
                    </td>
                    <td className="py-3 px-3">
                      <p className="text-slate-200 font-medium">{onu.odp_kode}</p>
                      <span className="text-[10px] text-slate-500">{onu.pon_port}</span>
                    </td>
                    <td className="py-3 px-3 text-slate-300">{onu.onu_model}</td>
                    <td className="py-3 px-3 font-mono text-slate-400">
                      {onu.distance_meters != null ? `~${onu.distance_meters} Meter` : "-"}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span
                        className={`inline-block font-mono font-bold text-xs px-2.5 py-0.5 rounded-full ${
                          onu.optical_state === "good"
                            ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                            : onu.optical_state === "warning"
                            ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                            : onu.optical_state === "critical"
                            ? "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                            : onu.optical_state === "offline"
                            ? "bg-slate-800 text-slate-400"
                            : "bg-slate-800/60 text-slate-500 border border-slate-700/60"
                        }`}
                        title={onu.rx_power_dbm == null && onu.optical_state !== "offline" ? "Firmware OLT ini belum membaca Rx via SNMP — hanya status link yang tersedia" : undefined}
                      >
                        {onu.optical_state === "offline"
                          ? "LOS / OFF"
                          : onu.rx_power_dbm != null
                          ? `${onu.rx_power_dbm} dBm`
                          : "-"}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      {onu.linked ? (
                      <button
                        onClick={() => handleReboot(onu.id, onu.sn)}
                        disabled={isRebooting}
                        data-testid={`reboot-onu-${onu.sn}`}
                        className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 ml-auto transition-colors border border-slate-700 disabled:opacity-50"
                      >
                        <RotateCw
                          className={`w-3 h-3 text-cyan-400 ${
                            isRebooting ? "animate-spin" : ""
                          }`}
                        />
                        <span>{isRebooting ? "Rebooting..." : "Reboot"}</span>
                      </button>
                      ) : (
                        <span className="text-[10px] text-slate-600">belum tertaut pelanggan</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
